import { faBuilding, faGraduationCap } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Form, Input, Row, Typography } from "antd";
import Link from "antd/lib/typography/Link";
import Text from "antd/lib/typography/Text";
import Title from "antd/lib/typography/Title";
import axios from "axios";
import { message } from "components/Messages/Messages";
import React, { useCallback, useState } from "react";

const { Paragraph } = Typography;

interface Props {
  handleClose: () => void;
}

const ResetPassword: React.FC<Props> = ({ handleClose }) => {
  const [studentUsername, setStudentUsername] = useState<string>("");
  const [userType, setUserType] = useState<"student" | "company">();

  const handleStudentSubmit = useCallback(async () => {
    if (!studentUsername) {
      message("error", "Vennligst skriv inn din e-postadresse.");
      return;
    }

    try {
      await axios.post("/students/request-password-reset/", { username: studentUsername });
      message("success", "Hvis denne mailen er registrert, vil du motta en tilbakestillingslenke");
      handleClose();
    } catch (err) {
      message("error", "Noe gikk galt. Vennligst prøv igjen senere");
    }
  }, [studentUsername, handleClose]);

  const handleCompanySubmit = useCallback(
    async (values: { email: string }) => {
      const { email } = values;
      if (!email) {
        message("error", "Vennligst skriv inn din e-postadresse.");
        return;
      }

      try {
        await axios.post("/companies/anonymous-reset-password/", { email });
        message(
          "success",
          <Paragraph>
            Dersom denne mailen er knyttet til en bedrift vil du motta et nytt passord på mail. Om du ikke mottar mail,
            vennligst kontakt{" "}
            <Link underline href="mailto:it@bindeleddet.no">
              it@bindeleddet.no
            </Link>
            .
          </Paragraph>,
          undefined,
          10
        );
        handleClose();
      } catch (err) {
        message("error", "Noe gikk galt. Vennligst prøv igjen senere");
      }
    },
    [handleClose]
  );

  return (
    <>
      <Title level={4} style={{ paddingBottom: "1EM" }}>
        Glemt brukernavn eller passord?
      </Title>

      {userType === "student" ? (
        <>
          <Text style={{ paddingBottom: "20px" }}>
            Brukernavnet til Bindeleddet-sidene er det samme som du bruker for å logge inn med FEIDE på NTNU.
          </Text>
          <Form onFinish={handleStudentSubmit} style={{ paddingBottom: "2EM" }}>
            <Title level={4} style={{ paddingTop: "1.5EM" }}>
              Få nytt passord:
            </Title>
            <Title level={5} style={{ paddingBottom: ".25EM" }}>
              Brukernavn:
            </Title>

            <Input
              style={{ paddingBottom: "1EM" }}
              type="text"
              id="email"
              value={studentUsername}
              onChange={e => setStudentUsername(e.target.value)}
              required
              addonAfter="@stud.ntnu.no"
            />
            <br />
            <Button type="primary" style={{ width: "100%" }} htmlType="submit">
              Send lenke på E-post
            </Button>
          </Form>
        </>
      ) : userType === "company" ? (
        <Col>
          <Title level={5}>Viktig informasjon om innlogging for bedrifter:</Title>
          <ul>
            <li>
              Bedrifter logger inn med <b>brukernavn</b> (ikke e-post) og passord.
            </li>
            <li>Brukernavn og passord er felles for hele bedriften.</li>
            <li>Det er kun kontaktpersoner som kan tilbakestille passordet.</li>
          </ul>
          <Title level={5} style={{ paddingTop: "1EM" }}>
            Tilbakestill passord:
          </Title>
          <Paragraph>
            Fyll ut skjemaet under for å tilbakestille passordet og få tilsendt et nytt passord på e-post.
          </Paragraph>
          <Form onFinish={handleCompanySubmit} style={{ paddingBottom: "2EM", paddingTop: "2EM" }} layout="vertical">
            <Form.Item
              label="E-postadresse"
              name="email"
              rules={[
                { required: true, message: "Vennligst skriv inn din e-postadresse" },
                { type: "email", message: "Vennligst skriv inn en gyldig e-postadresse" },
              ]}
            >
              <Input type="email" id="email" required />
            </Form.Item>
            <Button type="primary" style={{ width: "100%" }} htmlType="submit">
              Tilbakestill passord
            </Button>
          </Form>
          <Paragraph>
            Ved behov for ytterligere hjelp, ta kontakt med{" "}
            <Link underline href="mailto:it@bindeleddet.no">
              it@bindeleddet.no
            </Link>
            .
          </Paragraph>
        </Col>
      ) : (
        <Col>
          <Title level={5} style={{ paddingBottom: "1EM" }}>
            Er du student eller bedrift?
          </Title>
          <Row>
            <Col span={12}>
              <Button
                type="text"
                icon={<FontAwesomeIcon icon={faGraduationCap} />}
                onClick={() => {
                  setUserType("student");
                }}
                style={{ fontSize: "5em", width: "100%", height: "300px" }}
              >
                <Title level={4}>Student</Title>
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="text"
                icon={<FontAwesomeIcon icon={faBuilding} />}
                onClick={() => {
                  setUserType("company");
                }}
                style={{ fontSize: "5em", width: "100%", height: "300px" }}
              >
                <Title level={4}>Bedrift</Title>
              </Button>
            </Col>
          </Row>
        </Col>
      )}
    </>
  );
};

export default ResetPassword;
